export class DBBase {
    /**
     * @param {Object} component
     * @param {null|string} url
     */
    constructor(component, url) {
        this._component = component;
        this._url = url;

        this.reset();
    }

    /**
     * @return {void}
     */
    reset() {
        this.setConfig({});
        this.setParams({});

        this._beforeCallbacks = [];
        this._afterCallbacks = [];
        this._errorCallbacks = [];
    }

    /**
     * @param key
     * @param value
     * @returns {this}
     */
    addConfig(key, value) {
        this._config[key] = value;

        return this;
    }

    /**
     * @param {Object} config
     * @returns {this}
     */
    setConfig(config) {
        this._config = config;

        return this;
    }

    /**
     * @param {Object} params
     * @returns {this}
     */
    setParams(params) {
        this._params = params;

        return this;
    }

    /**
     * @param key
     * @param value
     * @returns {this}
     */
    addParam(key, value) {
        this._params[key] = value;

        return this;
    }

    /**
     * @param params
     * @returns {this}
     */
    addParams(params) {
        let keys = Object.keys(params);

        for (let i in keys) {
            if (!keys.hasOwnProperty(i)) continue;

            let key = keys[i];

            this._params[key] = params[key];
        }

        return this;
    }

    /**
     * @param {function(temp, instance)} callback
     * @returns {this}
     */
    before(callback) {
        this._beforeCallbacks.push(callback);

        return this;
    }

    /**
     * @param {function(response, instance)} callback
     * @returns {this}
     */
    after(callback) {
        this._afterCallbacks.push(callback);

        return this;
    }

    /**
     * @param {function(error, instance)} callback
     * @returns {this}
     */
    error(callback) {
        this._errorCallbacks.push(callback);

        return this;
    }

    /**
     * @param {Array.<function>} callbacks
     * @param {Object} response
     * @return {void}
     */
    runCallbacks(callbacks, response) {
        if (!callbacks.length) return;

        this._component.collect(callbacks).map((callback) => {
            callback(response, this);
        });
    }

    /**
     * @param {Array.<function>} callbacks
     * @param {Object} response
     * @return {void}
     */
    finish(callbacks, response) {
        this.runCallbacks(callbacks, response);
        this.reset();
    }

    /**
     * @param {Object} data
     * @param {int|null} id
     * @returns {Object|FormData}
     */
    clearSendData(data, id = null) {
        data = data || {};

        if (data instanceof FormData) {
            if (Object.keys(this._params).length) {
                this._component.collect(this._params).map((value, key) => {
                    data.append(key, typeof value === 'object' ? JSON.stringify(value) : value);
                });
            }

            if (id) {
                data.append('id', id);
            }
        } else {
            data = this._component.collect(data).filter((value, key) => {
                return value !== undefined && value !== null && value !== '';
            }).all();

            data = Object.assign(this._params, data);

            if (id) {
                data.id = id;
            }
        }

        return data;
    }

    /**
     * @param {string} path
     * @return Promise
     */
    request(path= '') {
        this.runCallbacks(this._beforeCallbacks, null);

        let params = this._params;

        if (!(this._params instanceof FormData)) {
            params = this._component.collect(params).filter((value, key) => {
                return value !== undefined && value !== null && value !== '';
            }).all();
        }

        return axios.post(this._url + path, params, this._config)
            .then((response) => {
                this.finish(this._afterCallbacks.reverse(), response);
            })
            .catch((error) => {
                if (error instanceof axios.Cancel) {
                    return;
                }

                console.error(error);
                console.error(error.response);

                if (error.response.data.message) {
                    (new ErrorAlert(this._component)).simple(error.response.data.message);
                } else if (error.response.status) {
                    (new ErrorAlert(this._component)).simple(error.response.status + ' - ' + error.response.statusText);
                }

                if (error.response.data.errors) {
                    this._component.collect(error.response.data.errors).map((errors, field) => {
                        this._component.collect(errors).map((error) => {
                            (new ErrorAlert(this._component)).simple(error);
                        });
                    });
                }

                this.finish(this._errorCallbacks, error);
            });
    }

    /**
     * @param {int|Array} ids
     */
    delete(ids) {
        this
            .setParams({ids: Array.isArray(ids) ? ids : [ids]})
            .after((response) => {
                (new SuccessAlert(this._component)).simple(response.data.message);
            })
            .request('/delete');
    }

    /**
     * @param {Object} data
     * @param {int|null} id
     */
    save(data, id = null) {
        this
            .setParams(this.clearSendData(data, id))
            .after(response => {
                (new SuccessAlert(this._component)).simple(response.data.message);
            })
            .request('/save');
    }

    /**
     * @param {Object} data
     * @param {int|null} id
     */
    update(data, id = null) {
        this
            .setParams(this.clearSendData(data, id))
            .after(response => {
                (new SuccessAlert(this._component)).simple(response.data.message);
            })
            .request('/update');
    }

    get() {
        this.request();
    }

    /**
     * @param {int} id
     */
    find(id) {
        this.request('/' + id);
    }
}
